import type { FC, ReactNode } from "react";

import { type HTMLMotionProps, motion } from "framer-motion";

export type HudActionTone = "neutral" | "success" | "danger";

interface ToneClasses {
  base: string;
  hover: string;
}

const TONES: Record<HudActionTone, ToneClasses> = {
  danger: {
    base: "border-rose-500/40 bg-rose-500/5 text-rose-600 dark:text-rose-300",
    hover: "hover:border-rose-500/70 hover:bg-rose-500/10",
  },
  neutral: {
    base: "border-slate-300 bg-transparent text-slate-700 dark:border-slate-700 dark:text-slate-200",
    hover:
      "hover:border-emerald-500/60 hover:bg-emerald-500/5 hover:text-emerald-700 dark:hover:border-emerald-400/40 dark:hover:bg-emerald-500/[0.06] dark:hover:text-emerald-300",
  },
  success: {
    base: "border-emerald-500/40 bg-emerald-500/5 text-emerald-700 dark:border-emerald-400/30 dark:bg-emerald-500/[0.06] dark:text-emerald-300",
    hover:
      "hover:border-emerald-500/70 hover:bg-emerald-500/10 dark:hover:border-emerald-400/60",
  },
};

interface HudActionButtonProps extends Omit<
  HTMLMotionProps<"button">,
  "children" | "ref"
> {
  label: string;
  tone?: HudActionTone;
  leftIcon?: ReactNode;
  loading?: boolean;
  /**
   * `md` (default) is the full-width layout geometry. `sm` matches the 240px
   * inspector rail's chip scale, which previously had no shared button and so
   * grew a private lookalike: the rail's own chips sat at px-1.5/py-0.5 and 9px
   * while anything reaching for this component arrived at px-3/py-1.5 and 10px,
   * so "More" and "Hibernate" rendered roughly twice the height of "Restart"
   * beside them. `sm` also drops the hover/tap scale, since neighbouring chips
   * do not animate and only the odd ones out appeared to move.
   */
  size?: "sm" | "md";
}

/** Bracketed-pill action button in the HUD-CRT dialect.
 *
 * Mirrors the Kill-button silhouette from InstanceDrawerHeader: thin border
 * + faint accent fill + mono uppercase tracking-widest, with framer-motion
 * scale-on-press feedback. Use for refresh, sign-out, retry, kill, cancel.
 */
export const HudActionButton: FC<HudActionButtonProps> = ({
  label,
  tone = "neutral",
  leftIcon,
  loading = false,
  disabled,
  className = "",
  size = "md",
  ...rest
}) => {
  const classes = TONES[tone];
  const compact = size === "sm";
  return (
    <motion.button
      type="button"
      whileHover={compact ? undefined : { scale: 1.02 }}
      whileTap={compact ? undefined : { scale: 0.97 }}
      disabled={disabled || loading}
      className={`flex flex-shrink-0 items-center gap-1.5 border font-mono font-bold uppercase tracking-widest transition-colors disabled:opacity-50 ${
        compact ? "px-1.5 py-0.5 text-[9px]" : "px-3 py-1.5 text-[10px]"
      } ${classes.base} ${classes.hover} ${className}`}
      {...rest}
    >
      {leftIcon && <span aria-hidden>{leftIcon}</span>}
      <span>{loading ? "…" : label}</span>
    </motion.button>
  );
};
